import React, { useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, Download, QrCode } from 'lucide-react';

/**
 * Admin-only tool that lives inside the Stats page. Lets Angela type an
 * organisation name (e.g. "SARC Manchester") and generates a QR code
 * pointing to `https://thefullimpact.org/?src=qr&ref=<slug>`.
 * Each scan is counted separately on the Stats page under
 * `arrival-partner-<slug>`, while still adding to the total `arrival-qr`
 * aggregate.
 *
 * All work happens in the browser — no new server endpoints.
 */

function slugify(input) {
  return String(input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

// Canonical prod origin — never leak the emergent preview host into a QR
// destined for print.
function canonicalOrigin() {
  if (typeof window === 'undefined') return 'https://thefullimpact.org';
  try {
    const origin = window.location.origin;
    const host = new URL(origin).hostname;
    if (/preview\.emergentagent\.com$/i.test(host)) {
      return 'https://thefullimpact.org';
    }
    return origin;
  } catch {
    return 'https://thefullimpact.org';
  }
}

export default function QRBuilder() {
  const [label, setLabel] = useState('');
  // When a preset chip is clicked we may want a slug that doesn't
  // match what slugify(label) would produce (e.g. "Manchester City" →
  // "manchestercity" not "manchester-city"). presetSlug overrides the
  // derived slug until the user types in the field again.
  const [presetSlug, setPresetSlug] = useState(null);
  const [copied, setCopied] = useState(false);
  const svgWrapRef = useRef(null);

  // Angela's already-generated QRs use these utm_source values. Offering
  // them as one-click chips so she doesn't have to retype them, and so
  // the slug matches the tag on QRs already in the wild.
  const PRESETS = [
    { key: 'gmp',            label: 'Greater Manchester Police' },
    { key: 'askforangela',   label: 'Ask for Angela' },
    { key: 'frsa',           label: 'FRSA' },
    { key: 'mopac',          label: 'MOPAC' },
    { key: 'manchestercity', label: 'Manchester City / matchday' },
    { key: 'sticker',        label: 'General stickers' },
    { key: 'leaflet',        label: 'General leaflets' },
    { key: 'general',        label: 'General Full Impact QR' },
  ];

  const derivedSlug = useMemo(() => slugify(label), [label]);
  const slug = presetSlug || derivedSlug;
  const origin = useMemo(canonicalOrigin, []);
  const url = slug ? `${origin}/?src=qr&ref=${slug}` : `${origin}/?src=qr`;
  const displayUrl = url.replace(/^https?:\/\//, '');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  const handleDownloadPng = () => {
    const svg = svgWrapRef.current?.querySelector('svg');
    if (!svg) return;
    // Clone so we can add xmlns for foreign renderers
    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const svgString = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const scale = 4; // ~800 px PNG for crisp print
      const size = 220 * scale;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(svgUrl);
      const pngUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = `tf-qr-${slug || 'default'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };
    img.src = svgUrl;
  };

  return (
    <section data-testid="stats-qr-builder">
      <div className="mt-12 mb-3 flex items-center gap-2">
        <QrCode size={15} color="var(--tf-purple-dark)" aria-hidden="true" />
        <p className="eyebrow">CUSTOM QR GENERATOR</p>
      </div>
      <p
        className="font-body mb-4"
        style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
      >
        Make a QR code for a specific organisation (e.g. an SARC, ISVA service or
        university welfare office). Each scan is counted separately under{' '}
        <em>QR by source</em> below, so you can see which partners are handing the
        code out.
      </p>

      <div className="tf-card p-5 sm:p-6" data-testid="qr-builder-card">
        <label
          className="font-body font-bold"
          htmlFor="qr-label"
          style={{ fontSize: 13, color: 'var(--tf-text)' }}
        >
          Organisation or campaign name
        </label>
        <input
          id="qr-label"
          data-testid="qr-label-input"
          type="text"
          placeholder="e.g. SARC Manchester"
          value={label}
          maxLength={60}
          onChange={(e) => { setLabel(e.target.value); setPresetSlug(null); }}
          className="tf-focus"
          style={{
            display: 'block',
            width: '100%',
            marginTop: 8,
            padding: '10px 14px',
            border: '1px solid var(--tf-card-border)',
            borderRadius: 12,
            fontSize: 14,
            fontFamily: 'inherit',
            color: 'var(--tf-text)',
            background: '#fff',
          }}
        />
        <p
          className="font-body"
          style={{
            fontSize: 12,
            color: 'var(--tf-text-muted)',
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          Slug: <code data-testid="qr-slug-preview">{slug || '(empty)'}</code> — this
          is the value that will appear in your stats.
        </p>

        {/* Known partners — one-click chips */}
        <p
          className="font-body font-bold mt-5"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.22em',
            color: 'var(--tf-purple-dark)',
          }}
        >
          KNOWN PARTNERS
        </p>
        <p
          className="font-body"
          style={{
            fontSize: 12,
            color: 'var(--tf-text-muted)',
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          One-click chips for the QRs already out in the wild. Any of these will match
          existing UTM tags on printed materials.
        </p>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="qr-presets">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              data-testid={`qr-preset-${p.key}`}
              onClick={() => { setLabel(p.label); setPresetSlug(p.key); }}
              className="tf-focus"
              style={{
                background: slug === p.key ? 'var(--tf-purple)' : '#fff',
                color: slug === p.key ? '#fff' : 'var(--tf-purple-dark)',
                border: '1px solid var(--tf-purple-light)',
                borderRadius: 999,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-5 sm:items-start">
          <div
            ref={svgWrapRef}
            data-testid="qr-preview"
            className="flex-shrink-0 mx-auto sm:mx-0 p-3 rounded-xl"
            style={{
              background: 'var(--tf-bg-secondary)',
              border: '1px solid var(--tf-card-border)',
              lineHeight: 0,
            }}
          >
            <div className="p-2 rounded-lg bg-white">
              <QRCodeSVG
                value={url}
                size={180}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#3F1F5B"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              LINK ENCODED IN THE QR
            </p>
            <p
              className="font-body mt-2"
              data-testid="qr-encoded-url"
              style={{
                fontSize: 12.5,
                lineHeight: 1.5,
                color: 'var(--tf-text)',
                wordBreak: 'break-all',
                background: 'var(--tf-bg-secondary)',
                border: '1px solid var(--tf-card-border)',
                borderRadius: 10,
                padding: '10px 12px',
              }}
            >
              {displayUrl}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCopy}
                data-testid="qr-copy-button"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold tf-focus"
                style={{
                  background: copied ? 'var(--tf-purple)' : '#fff',
                  color: copied ? '#fff' : 'var(--tf-purple-dark)',
                  border: '1px solid var(--tf-purple-light)',
                  fontSize: 12.5,
                  minHeight: 36,
                }}
              >
                {copied ? (
                  <>
                    <Check size={13} strokeWidth={2.4} /> Link copied
                  </>
                ) : (
                  <>
                    <Copy size={13} strokeWidth={2.2} /> Copy link
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleDownloadPng}
                data-testid="qr-download-button"
                disabled={!slug}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold tf-focus"
                style={{
                  background: slug ? 'var(--tf-purple)' : '#E7E1EF',
                  color: slug ? '#fff' : 'var(--tf-text-muted)',
                  border: 'none',
                  fontSize: 12.5,
                  minHeight: 36,
                  cursor: slug ? 'pointer' : 'not-allowed',
                }}
              >
                <Download size={13} strokeWidth={2.2} /> Download PNG
              </button>
            </div>
            <p
              className="font-body"
              style={{
                fontSize: 11.5,
                color: 'var(--tf-text-muted)',
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              Enter a name and the Download button becomes active. The PNG saves at
              ~800 × 800 px — big enough for print. Send the same QR to the same
              organisation each time to keep their counter accurate.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
